import React, { useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

export const REPORT_TABS = [
  { id: 'overview', label: 'Overview', hint: 'Your type' },
  { id: 'week', label: 'This week', hint: 'Playbook' },
  { id: 'team', label: 'Team fit', hint: 'Co-founders' },
  { id: 'tools', label: 'Share & save', hint: 'Export' },
];

const NavWrap = styled.nav`
  width: 100%;
  max-width: 900px;
  margin: 0 auto 1.75rem;
  position: sticky;
  top: 0.75rem;
  z-index: 10;
  padding: 0.35rem;
  border-radius: 16px;
  background: rgba(10, 14, 26, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
`;

const TabList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.25rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.35rem;
  }
`;

const TabButton = styled(motion.button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  padding: 0.75rem 0.5rem;
  border-radius: 12px;
  border: 1px solid ${(props) => (props.$active ? `${props.$accent}55` : 'transparent')};
  background: ${(props) =>
    props.$active
      ? `${props.$accent}20`
      : 'transparent'};
  color: ${(props) => (props.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  cursor: pointer;
  min-height: 56px;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? undefined : 'rgba(255, 255, 255, 0.05)'};
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--type-accent, var(--primary-color));
    outline-offset: 2px;
  }
`;

const TabLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
  text-align: center;

  @media (min-width: 641px) {
    font-size: 0.9rem;
  }
`;

const TabHint = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${(props) => (props.$active ? 'var(--type-accent, var(--primary-light))' : 'rgba(255,255,255,0.45)')};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const TabPanel = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ResultsTabNav = ({ activeTab, onTabChange }) => {
  const tabIds = REPORT_TABS.map((t) => t.id);

  const focusTab = useCallback((tabId) => {
    const el = document.getElementById(`report-tab-${tabId}`);
    if (el) el.focus();
  }, []);

  const handleKeyDown = (event, tabId) => {
    const idx = tabIds.indexOf(tabId);
    if (idx < 0) return;

    let nextIdx = idx;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIdx = (idx + 1) % tabIds.length;
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIdx = (idx - 1 + tabIds.length) % tabIds.length;
      event.preventDefault();
    } else if (event.key === 'Home') {
      nextIdx = 0;
      event.preventDefault();
    } else if (event.key === 'End') {
      nextIdx = tabIds.length - 1;
      event.preventDefault();
    } else {
      return;
    }

    const nextTab = tabIds[nextIdx];
    onTabChange(nextTab);
    focusTab(nextTab);
  };

  return (
  <NavWrap aria-label="Report sections">
    <TabList role="tablist">
      {REPORT_TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <TabButton
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`report-panel-${tab.id}`}
            id={`report-tab-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            $active={isActive}
            $accent="var(--type-accent, var(--primary-color))"
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, tab.id)}
            whileTap={{ scale: 0.98 }}
          >
            <TabLabel>{tab.label}</TabLabel>
            <TabHint $active={isActive}>{tab.hint}</TabHint>
          </TabButton>
        );
      })}
    </TabList>
  </NavWrap>
  );
};

export const ResultsTabPanel = ({ tabId, activeTab, children, variants }) => {
  if (activeTab !== tabId) return null;

  return (
    <TabPanel
      id={`report-panel-${tabId}`}
      role="tabpanel"
      aria-labelledby={`report-tab-${tabId}`}
      key={tabId}
      variants={variants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </TabPanel>
  );
};

export default ResultsTabNav;
